/**
 * Remove this computer: what the owner is told about a removal, and the one local command
 * for a computer Brunel could not reach.
 *
 * The states are the database's (brunel.node_removal_status) and are never merged: an
 * instruction that was delivered is not a cleanup that was verified, and a computer that went
 * quiet is said to have gone quiet.
 */

export type RemovalState =
  | 'waiting_for_computer' | 'cleanup_started' | 'lost_contact'
  | 'local_cleanup_needed' | 'cleanup_incomplete' | 'cleanup_verified';

export interface NodeRemovalRow {
  readonly org_id: string;
  readonly id: string;
  readonly node_id: string;
  readonly node_name: string;
  readonly host_kind: string | null;
  readonly requested_at: string;
  readonly reachable_at_request: boolean;
  readonly delivered_at: string | null;
  readonly reported_at: string | null;
  readonly cleanup_verified: boolean | null;
  readonly remaining: readonly string[];
  readonly kept: readonly string[];
  readonly state: RemovalState;
}

export const NODE_REMOVAL_COLUMNS =
  'org_id,id,node_id,node_name,host_kind,requested_at,reachable_at_request,delivered_at,reported_at,cleanup_verified,remaining,kept,state';

export interface RemovalView {
  readonly label: string;
  readonly detail: string;
  readonly tone: 'ok' | 'waiting' | 'attention';
  /** Show the local command: Brunel cannot finish, or cannot confirm, the cleanup itself. */
  readonly localCommand: boolean;
  /** Nothing more will change without the owner. */
  readonly settled: boolean;
}

const REMAINING_LABELS: Readonly<Record<string, string>> = {
  'service.node': 'the background service',
  'service.overseer': 'the Overseer launch agent',
  'process.brunel': 'a running Brunel process',
  'process.unknown': 'running processes (could not be checked)',
  'path.install': 'the install folder',
  'path.overseer': 'the Overseer folder',
  'path.logs': 'the logs',
  'path.state': 'the profile or credentials',
  'credential.overseer_database': 'the Overseer database credential',
  'credential.planner_key': 'the planner credential',
  'credential.cli_broker': 'the database CLI credential',
};

export function remainingText(keys: readonly string[]): string {
  return keys.map((key) => REMAINING_LABELS[key] ?? key).join(', ');
}

export function removalView(row: Pick<NodeRemovalRow, 'state' | 'remaining' | 'kept' | 'node_name'>): RemovalView {
  const kept = row.kept.includes('path.runs') ? ' Project work folders were kept.' : '';
  switch (row.state) {
    case 'cleanup_verified':
      return {
        label: 'Removed and cleaned up', tone: 'ok', localCommand: false, settled: true,
        detail: `${row.node_name} checked itself after cleanup and found nothing of Brunel left running or installed.${kept}`,
      };
    case 'cleanup_incomplete':
      return {
        label: 'Removed; cleanup incomplete', tone: 'attention', localCommand: true, settled: true,
        detail: `${row.node_name} reported that this is still there: ${remainingText(row.remaining) || 'unknown'}. Run the command on it to finish.`,
      };
    case 'cleanup_started':
      return {
        label: 'Removed; cleanup started', tone: 'waiting', localCommand: false, settled: false,
        detail: `${row.node_name} received the uninstall instruction and started cleaning up. It has not yet confirmed the result.`,
      };
    case 'lost_contact':
      return {
        label: 'Removed; cleanup not confirmed', tone: 'attention', localCommand: true, settled: true,
        detail: `${row.node_name} started cleaning up and was not heard from again, so the cleanup is not verified. Run the command on it to check and finish.`,
      };
    case 'waiting_for_computer':
      return {
        label: 'Removed; waiting for the computer', tone: 'waiting', localCommand: false, settled: false,
        detail: `${row.node_name} takes no more work. The uninstall instruction is waiting for it to pick up.`,
      };
    case 'local_cleanup_needed':
      return {
        label: 'Removed; local cleanup needed', tone: 'attention', localCommand: true, settled: true,
        detail: `${row.node_name} takes no more work, but it did not pick up the uninstall instruction. Run the command on it to remove Brunel from it.`,
      };
  }
}

/**
 * The one command for the computer itself. It fetches the current node program to a temporary
 * file and runs its `uninstall`, which uses the session already on that computer to report -
 * so it carries no code, key or address of its own.
 */
export function localUninstallCommand(origin: string, hostKind: string | null): { readonly shell: string; readonly command: string } {
  const base = origin.replace(/\/+$/, '');
  if (hostKind === 'windows') {
    return {
      shell: 'PowerShell',
      command: `$f = Join-Path $env:TEMP 'brunel-node-uninstall.mjs'; Invoke-WebRequest -UseBasicParsing -Uri '${base}/node/bin/brunel-node.mjs' -OutFile $f; node $f uninstall; Remove-Item $f`,
    };
  }
  return {
    shell: 'Terminal',
    command: `f="\${TMPDIR:-/tmp}/brunel-node-uninstall.mjs"; curl -fsSL '${base}/node/bin/brunel-node.mjs' -o "$f" && node "$f" uninstall; rm -f "$f"`,
  };
}

const REMOVE_ERRORS: Readonly<Record<string, string>> = {
  confirmation_mismatch: 'That is not this computer\'s name. Nothing was removed.',
  node_busy: 'This computer is holding a run. Stop it or let it finish, then remove the computer.',
  holds_management_role: 'This computer holds a management role. Move that role to another computer first.',
  not_removable: 'This device signs in as you and cannot be removed here.',
  forbidden: 'Removing a computer needs your verified owner session.',
  not_found: 'That computer no longer exists.',
};

export interface RemoveComputerResult {
  readonly ok: boolean;
  readonly message: string;
}

type RpcAnswer = { ok?: boolean; value?: { reachable?: boolean; alreadyRemoved?: boolean }; error?: { code?: string } } | null;

/**
 * Remove one computer through the owner's AAL2 guard, with the name the owner typed as the
 * confirmation the database checks. Never replayed; the room re-reads after every outcome.
 */
export async function removeComputer(input: {
  readonly orgId: string;
  readonly nodeId: string;
  readonly nodeName: string;
  readonly confirmName: string;
  readonly guard: (name: 'post_owner_command', action: () => Promise<void>) => Promise<void>;
  readonly rpc: (name: 'remove_computer', args: { readonly request: unknown }) => PromiseLike<{ data: unknown; error: unknown }>;
  readonly refresh: () => Promise<void>;
  readonly onResult: (result: RemoveComputerResult) => void;
}): Promise<void> {
  await input.guard('post_owner_command', async () => {
    let answer: RpcAnswer = null;
    let failed = false;
    try {
      const { data, error } = await input.rpc('remove_computer', {
        request: { orgId: input.orgId, nodeId: input.nodeId, confirmName: input.confirmName },
      });
      answer = data as RpcAnswer;
      failed = Boolean(error);
    } catch {
      failed = true;
    }
    if (!failed && answer?.ok === true) {
      input.onResult({
        ok: true,
        message: answer.value?.reachable === true
          ? `${input.nodeName} was removed. It takes no more work, and it has been told to clean itself up; the result appears below when it reports.`
          : `${input.nodeName} was removed and takes no more work. It was not reachable, so run the command below on it to finish the cleanup.`,
      });
    } else {
      const code = answer?.error?.code ?? '';
      input.onResult({ ok: false, message: REMOVE_ERRORS[code] ?? 'The computer was not removed. Try again.' });
    }
    await input.refresh();
  });
}
